'use client';
import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@ahafood/ui/components/dialog';
import {
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerDescription,
  Drawer,
} from '@ahafood/ui/components/drawer';
import { ScrollArea } from '@ahafood/ui/components/scroll-area';
import { formatCurrency } from '@ahm/common-helpers';
import { useMemo, useCallback, useEffect } from 'react';
import { CartItem } from '../../order/[storeId]/cart/cart-item';
import { useStoreItemSheet } from './store-item-sheet-context';
import type { Item } from '@/api/use-get-menu-order';
import { useBottomPosition } from '@/hooks/use-bottom-position';
import { useIsMobile } from '@/lib/is-mobile';
import type { CartStore } from '@/store/cart/cart-store';
import { useCartStore } from '@/store/cart/cart-store-provider';
import type { MenuItem } from '@/types/item';

const convertToMenuItem = (item: Item | null): MenuItem | null => {
  if (!item) return null;

  const {
    _id,
    name,
    price,
    discounted_price,
    description,
    status,
    image_thumb_url = '',
    discount_config,
  } = item;

  return {
    _id,
    name,
    price,
    discounted_price,
    description,
    status: status as 'ACTIVE' | 'INACTIVE',
    image_thumb_url,
    discount_config: discount_config
      ? {
          type: ['VALUE', 'PERCENTAGE'].includes(discount_config.type)
            ? (discount_config.type as 'VALUE' | 'PERCENTAGE')
            : 'VALUE',
          value: discount_config.value,
        }
      : undefined,
  };
};

type EditItemProps = {
  item: Item;
  isOpen: boolean;
  onClose: () => void;
  mode?: 'webview' | 'webstore';
};

export function EditItem({ item, isOpen, onClose, mode }: EditItemProps) {
  const isMobile = useIsMobile();
  const cartItemsSelector = useMemo(
    () => (state: CartStore) => state.items,
    []
  );
  const cartItems = useCartStore(cartItemsSelector);
  const { openSheet } = useStoreItemSheet();

  const matchingCartItems = useMemo(() => {
    return item
      ? Object.values(cartItems).filter(
          (cartItem) => Number(cartItem._id) === item._id
        )
      : [];
  }, [cartItems, item]);

  const { bottomPosition } = useBottomPosition();

  const handleAddItem = useCallback(() => {
    const menuItem = convertToMenuItem(item);
    if (menuItem) {
      menuItem.emptyCustomization = true;
    }
    openSheet(menuItem);
    onClose();
  }, [item, openSheet, onClose]);

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const handleSheetClose = () => {};

    document.addEventListener('sheet-closed', handleSheetClose);

    return () => {
      document.removeEventListener('sheet-closed', handleSheetClose);
    };
  }, []);

  if (!item || !isOpen) return null;

  const contentJSX = (
    <>
      {item.discount_config && (
        <div className="text-sm text-blue-50 py-2 px-4">
          {item.discount_config.type === 'VALUE'
            ? `Giảm ${formatCurrency(item.discount_config.value)}`
            : item.discount_config.type === 'PERCENT'
              ? `Giảm ${item.discount_config.value}%`
              : `Đồng giá ${formatCurrency(item.discount_config.value)}`}
        </div>
      )}

      {matchingCartItems.length > 0
        ? matchingCartItems.map((cartItem) => {
            const formattedItem = {
              _id: String(cartItem._id),
              name: cartItem.name,
              price: cartItem.price,
              quantity: cartItem.quantity,
              customizations: cartItem.customizations,
              cartItemId: cartItem.cartItemId,
            };

            return (
              <CartItem
                key={cartItem.cartItemId}
                item={formattedItem as any}
                onClose={onClose}
              />
            );
          })
        : null}
    </>
  );

  if (mode === 'webstore' && !isMobile) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="w-full max-w-lg mx-auto max-h-[80vh] flex flex-col p-4">
          <DialogHeader className="border-b flex flex-row flex-none items-center justify-between text-neutral-90 pb-4">
            <div className="flex-1 text-start font-semibold text-lg">
              {item.name}
            </div>
            <div className="flex-none flex flex-col text-neutral-90 items-end">
              <span className="text-xs">Giá gốc</span>
              <span className="text-sm font-bold">
                {formatCurrency(item.price)}
              </span>
            </div>
          </DialogHeader>

          <ScrollArea className="flex-1 px-0">
            <div className="flex-1 overflow-auto">{contentJSX}</div>
          </ScrollArea>

          <div className="py-4 border-t shrink-0">
            <Button className="w-full font-bold" onClick={handleAddItem}>
              Thêm món
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent
        className="w-full max-w-lg mx-auto"
        style={{ marginBottom: bottomPosition }}
      >
        <DrawerHeader className="border-b flex flex-row flex-none items-center justify-between text-neutral-90">
          <DrawerTitle className="flex-1 text-start">{item.name}</DrawerTitle>
          <DrawerDescription className="flex-none flex flex-col text-neutral-90 items-end">
            <span className="text-xs">Giá gốc</span>
            <span className="text-sm font-bold">
              {formatCurrency(item.price)}
            </span>
          </DrawerDescription>
        </DrawerHeader>

        <ScrollArea className="flex-1">
          <div className="flex-1 overflow-auto">{contentJSX}</div>
        </ScrollArea>

        <div className="p-4 border-t shrink-0">
          <Button className="w-full font-bold" onClick={handleAddItem}>
            Thêm món
          </Button>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
